/*
 * 1-Minute Maths
 * Copyright © 2021 - 2024 White Rose Maths
 * index.tsx - components for the app's navigation
 */

import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import ActionBarImage from '../ActionBarImage';
import CategoryScreen from '../components/Category/CategoryScreen';
import CustomHeaderBackImage from '../components/CustomHeaderBackImage';
import HomeScreen from '../components/Home/HomeScreen';
import QuestionsScreen from '../components/Question/QuestionsScreen';
import ResultsScreen from '../components/Results/ResultsScreen';
import SubitisingScreen from '../components/Subitising/SubitisingScreen';
import type { RootStackParamList, RootStackProps } from './types';
import BreakDownScreen from '../components/Results/BreakDownScreen';
import { getDefaultHeaderHeight } from '@react-navigation/elements';
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context';
import { type ColorValue, Platform } from 'react-native';
import { containAspectRatio } from '../scale';
import { useWindowDimensions } from 'react-native';
import { type ComponentClass } from 'react';

/**
 * The navigation model currently consists of a single stack navigator "RootStack". There are no nested navigators.
 *
 * In order to understand what's going on here, it helps to define some navigation concepts:
 *
 * - Screen: a component which renders content to the screen, and is the destination of some route
 * - Route: has a unique name, and corresponds to either a screen or (in the case of nested navigators) a navigator
 * - Navigator: a component which manages navigation to several routes, e.g. in a stack, or with tabs
 * - Params: configuration that each component needs
 * - Props: the actual properties passed to the component (screen or navigator), which contains:
 *   - a `navigation` object, for performing subsequent navigations
 *   - a `route` object, which contains the params
 */
export default function Navigation({onReady}: {onReady?: () => void}) {
  return (
    <NavigationContainer onReady={onReady}>
      <StackNavigator />
    </NavigationContainer>
  );
}

const RootStack = createStackNavigator<RootStackParamList>();

function StackNavigator() {
  // For native, we use useSafeAreaFrame. This is roughly equivalent to useWindowDimensions, except the latter is
  // bugged on Android to not include the system bar even though our app extends into that system bar.
  // useSafeAreaFrame doesn't work for web, so just fall back to useWindowDimensions.
  // Hooks shouldn't be called conditionally if the condition changes, but this condition cannot change.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const windowDimensions = Platform.OS === 'web' ? useWindowDimensions() : useSafeAreaFrame();
  const insets = useSafeAreaInsets();

  const defaultHeaderHeight = getDefaultHeaderHeight(windowDimensions, false, insets.top);
  // Remove the insets for the status bar (if any), we add it back on later
  let headerHeight = defaultHeaderHeight - insets.top;

  // react-navigation's default settings are ios: 17, android: 20, default: 18.
  // We could do exactly this, but 18 looks the best overall
  let headerFontSize = 18;
  let backImageHeight = 40;
  let logoHeight = 40;

  if (Platform.OS === 'web') {
    // On web, scale the header. It has an aspect ratio of at least 6 and fits into the top 10% of the screen.
    headerHeight = containAspectRatio({width: windowDimensions.width, height: 0.1*windowDimensions.height}, 6).height;
    // Scale all other quantities in relation to the header height. To make it look similar to native, we assume that
    // the header on native was about 56dp excluding insets.
    const scaleFactor = headerHeight/56;
    headerFontSize *= scaleFactor;
    backImageHeight *= scaleFactor;
    logoHeight *= scaleFactor;
  }

  const headerOptions = (title: string, backgroundColor: ColorValue, textColor: ColorValue, showLogo = true) => ({
    title: title,
    headerBackImage: () => <CustomHeaderBackImage height={backImageHeight}/>,
    headerBackTitleVisible: false,
    headerStyle: {
      height: headerHeight + insets.top,
      backgroundColor: backgroundColor,
    },
    // Defaults are for iOS to use 'center' and Android to use 'left'. We set them to be the same for consistency.
    headerTitleAlign: 'left' as const,
    headerTitleStyle: {
      color: textColor,
      fontSize: headerFontSize,
      fontFamily: 'WhiteRoseMathsFont',
      // For some reason the title doesn't always make the right amount of space for the headerRight, so it displays
      // underneath it. This number was found empirically, to ensure that the title is truncated before the headerRight.
      // If you change the headerRight, this value may no longer work.
      marginRight: logoHeight * 1.1,
    },
    headerTitleAllowFontScaling: false,
    headerRight: showLogo ? () => <ActionBarImage height={logoHeight} /> : undefined
  });

  return (
    <RootStack.Navigator
      initialRouteName="Home"
      screenOptions={{ gestureEnabled: false }}
    >
      <RootStack.Screen
        name="Home"
        component={HomeScreen}
        options={{
          title: '1-Minute Maths',
          headerShown: false,
        }}
      />

      <RootStack.Screen
        name="Category"
        component={CategoryScreen}
        options={({ route }) => headerOptions(route.params.name, route.params.color, 'black', false)}
      />
      <RootStack.Screen
        name="Questions"
        component={QuestionsScreen}
        options={({ route }) => headerOptions(route.params.name, route.params.color, 'white')}
      />
      <RootStack.Screen
        name="Subitising"
        component={SubitisingScreen as ComponentClass<RootStackProps<'Subitising'>, unknown>}
        options={({ route }) => headerOptions(route.params.name, route.params.color, 'white')}
      />
      <RootStack.Screen
        name="Results"
        component={ResultsScreen}
        options={headerOptions('Results', '#56B8C2', 'white')}
      />

      <RootStack.Screen
        name="BreakDown"
        component={BreakDownScreen}
        options={headerOptions('Answers', '#56B8C2', 'white')}
      />
    </RootStack.Navigator>
  );
}
